// The condition strip, per-condition visibility and the live counter on the Layout Canvas — pure logic.
//
// Two key spaces exist and must not be mixed up:
//   catalog key  `item:7` | `asm:9` | `ct:3`        — a condition TYPE in the picker (conditionTypes[].TypeKey); what arms a tool
//   drawn key    `item:7` | `assembly:9` | `lib:3`  — what a DRAWN row is an instance of (conditionTypeKey(row) in the canvas)
// Pinned conditions are catalog keys (they arm tools); visibility and totals are by drawn key (they filter rows).
// Everything here is a per-viewer view preference or a computed readout — nothing is stored with the estimate.

export const MAX_PINS = 9;
export const PINS_STORAGE_KEY = "cs.takeoff.pinnedConditions";
export const COUNTER_STORAGE_KEY = "cs.takeoff.liveCounter";

// ── key spaces ───────────────────────────────────────────────────────────────
/** catalog key -> the drawn key rows of that type carry (null for a type no drawn row can reference). */
export function drawnKeyOfCatalog(typeKey) {
  if (typeof typeKey !== "string") return null;
  if (typeKey.startsWith("item:")) return typeKey;
  if (typeKey.startsWith("asm:")) return `assembly:${typeKey.slice(4)}`;
  if (typeKey.startsWith("ct:")) return `lib:${typeKey.slice(3)}`;
  return null;
}
/** drawn key -> catalog key (null for `type:` and `unassigned`, which are not in the picker). */
export function catalogKeyOfDrawn(drawnKey) {
  if (typeof drawnKey !== "string") return null;
  if (drawnKey.startsWith("item:")) return drawnKey;
  if (drawnKey.startsWith("assembly:")) return `asm:${drawnKey.slice(9)}`;
  if (drawnKey.startsWith("lib:")) return `ct:${drawnKey.slice(4)}`;
  return null;
}

// ── pinned strip ─────────────────────────────────────────────────────────────
export function readPins(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try {
    const v = JSON.parse(storage?.getItem(PINS_STORAGE_KEY) || "[]");
    return Array.isArray(v) ? [...new Set(v.filter(k => typeof k === "string"))].slice(0, MAX_PINS) : [];
  } catch { return []; }
}
export function writePins(pins, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(PINS_STORAGE_KEY, JSON.stringify(pins.slice(0, MAX_PINS))); } catch { /* not remembered */ }
}

/** Pin or unpin a type. -> { pins, full } — `full` when it is not pinned because nine already are. */
export function togglePin(pins, typeKey) {
  if (pins.includes(typeKey)) return { pins: pins.filter(k => k !== typeKey), full: false };
  if (pins.length >= MAX_PINS) return { pins, full: true };
  return { pins: [...pins, typeKey], full: false };
}

/**
 * The strip as shown: pin number (1–9, by position) -> the type. A pin whose type is no longer in the catalog is
 * left out of the strip (not removed from storage — a catalog that failed to load must not wipe the person's pins).
 * Positions are of the pins that resolve, so keys 1–9 always arm what the strip shows.
 */
export function resolvePins(pins, conditionTypes) {
  const byKey = new Map((conditionTypes || []).map(t => [t.TypeKey, t]));
  return pins.map(k => byKey.get(k)).filter(Boolean).map((type, i) => ({ digit: i + 1, type }));
}
export const typeForDigit = (resolved, digit) => resolved.find(r => r.digit === digit)?.type || null;

// ── per-condition visibility ─────────────────────────────────────────────────
/** Hide / show one drawn type. */
export function toggleHidden(hidden, drawnKey) {
  const next = new Set(hidden);
  if (next.has(drawnKey)) next.delete(drawnKey); else next.add(drawnKey);
  return next;
}
/** Show ONLY this type; doing it again (it is already the only one visible) shows everything. */
export function isolate(hidden, drawnKey, allKeys) {
  const others = allKeys.filter(k => k !== drawnKey);
  const alreadyIsolated = !hidden.has(drawnKey) && others.every(k => hidden.has(k));
  return alreadyIsolated ? new Set() : new Set(others);
}
/** "2 of 7 hidden" (null when nothing is hidden). Counts only types that exist on this drawing. */
export function hiddenSummary(hidden, allKeys) {
  const n = allKeys.filter(k => hidden.has(k)).length;
  return n ? `${n} of ${allKeys.length} hidden` : null;
}

// ── live counter ─────────────────────────────────────────────────────────────
/**
 * Running totals per condition type: [{ key, label, color, style, totals: [{ u, q }], count }], biggest cost-code order
 * kept by first appearance. `quantitiesOf(row)` -> [{ q, u }] is the canvas's own (computeConditionQuantities), so the
 * counter always agrees with the labels. Back-outs and rows with no type are left out.
 */
export function conditionTotals(rows, { keyOf, quantitiesOf, hidden = new Set() } = {}) {
  const map = new Map();
  for (const c of rows || []) {
    if (c.MeasureRole === "deduct") continue;
    const key = keyOf(c);
    if (!key || key === "unassigned" || hidden.has(key)) continue;
    if (!map.has(key)) map.set(key, { key, label: c.Code || c.Name || key, color: c.Color || null, style: c.Style || "linear", totals: new Map(), count: 0 });
    const g = map.get(key);
    g.count += 1;
    for (const { q, u } of quantitiesOf(c)) if (Number.isFinite(q)) g.totals.set(u, (g.totals.get(u) || 0) + q);
  }
  return [...map.values()].map(g => ({ ...g, totals: [...g.totals.entries()].map(([u, q]) => ({ u, q })) }));
}

export function readCounterOpen(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { return storage?.getItem(COUNTER_STORAGE_KEY) === "1"; } catch { return false; }
}
export function writeCounterOpen(open, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(COUNTER_STORAGE_KEY, open ? "1" : "0"); } catch { /* not remembered */ }
}
